import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectRecipes, selectNextUrl } from "../../redux/selectors/selectors";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import {
  openPopupAction,
  closePopupAction,
  setSelectedRecipeAction,
} from "../../redux/actions/actions";
import { startRecipeSearchThunk } from "../../redux/thunks/search_recipe_thunk";
import Container from "@mui/material/Container";
import CardMedia from "@mui/material/CardMedia";

const Recipes = ({ onRecipesRender }) => {
  const dispatch = useDispatch();
  const recipes = useSelector(selectRecipes);
  const openPopup = useSelector((state) => state.popup.open);
  const selectedRecipe = useSelector((state) => state.popup.selectedRecipe);
  const nextPageUrl = useSelector(selectNextUrl);

  const handleCardClick = (recipe) => {
    dispatch(openPopupAction());
    dispatch(setSelectedRecipeAction(recipe));
  };

  const handleClosePopup = () => {
    dispatch(closePopupAction());
  };

  useEffect(() => {
    if (onRecipesRender) {
      onRecipesRender();
    }
  }, [onRecipesRender]);

  return (
    <Container maxWidth="lg">
      <Grid container align="center" spacing={4} style={{ marginTop: "20px" }}>
        {recipes.map((res, idx) => (
          <Grid
            item
            xs={12}
            sm={4}
            ms={4}
            key={idx}
            style={{ display: "flex" }}
          >
            <Card
              elevation={0}
              onClick={() => handleCardClick(res.recipe)}
              data-testid="clickable-card"
              sx={{
                flex: 1,
                height: "100%",
                borderRadius: 3,
                overflow: "hidden",
                border: "4px solid white",
                background:
                  "linear-gradient(145deg, rgba(20,20,20,0.92), rgba(10,10,10,0.92))",
                color: "white",
                boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
                transition:
                  "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 18px 45px rgba(0,0,0,0.55)",
                  borderColor: "rgba(255,255,255,0.22)",
                },
              }}
            >
              <CardActionArea sx={{ height: "100%" }}>
                {/* Image section */}
                <div style={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="260"
                    image={res.recipe.image}
                    alt={res.recipe.label}
                    style={{ objectFit: "cover" }}
                  />

                  {/* Gradient overlay */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 100%)",
                      pointerEvents: "none",
                    }}
                  />

                  {/* Title on image */}
                  <div
                    style={{
                      position: "absolute",
                      left: 16,
                      right: 16,
                      bottom: 14,
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: 0.2,
                        textShadow: "0 6px 18px rgba(0,0,0,0.55)",
                        lineHeight: 1.15,
                      }}
                    >
                      {res.recipe.label}
                    </Typography>

                    {/* Quick chips */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 12,
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.12)",
                          backdropFilter: "blur(8px)",
                        }}
                      >
                        {Math.round(res.recipe.calories)} cal
                      </span>

                      <span
                        style={{
                          fontSize: 12,
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.12)",
                          backdropFilter: "blur(8px)",
                        }}
                      >
                        {res.recipe.ingredientLines?.length ?? 0} ingredients
                      </span>

                      {res.recipe.cuisineType?.[0] && (
                        <span
                          style={{
                            fontSize: 12,
                            padding: "6px 10px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.12)",
                            backdropFilter: "blur(8px)",
                            textTransform: "capitalize",
                          }}
                        >
                          {res.recipe.cuisineType[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content section */}
                <CardContent sx={{ pt: 2.2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.85,
                      mb: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {res.recipe.healthLabels?.slice(0, 3).join(" • ") ||
                      "Tap to view details"}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      fontWeight: 700,
                      color: "#ffb74d",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    View recipe →
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Button
        onClick={() => dispatch(startRecipeSearchThunk(nextPageUrl))}
        disabled={!nextPageUrl}
        variant="contained"
        sx={{
          mt: 3,
          mx: "auto",
          px: 4,
          py: 1.3,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 3,
          fontWeight: 900,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "black",
          background: "linear-gradient(90deg, #ffb74d, #ffa726)",
          boxShadow: "0 14px 30px rgba(0,0,0,0.35)",
          transition:
            "transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
          "&:hover": {
            background: "linear-gradient(90deg, #ffc266, #ffb74d)",
            boxShadow: "0 18px 40px rgba(0,0,0,0.55)",
            transform: "translateY(-2px)",
          },
          "&.Mui-disabled": {
            background: "rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.45)",
            boxShadow: "none",
          },
        }}
      >
        Load More Recipes
      </Button>

      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            background:
              "linear-gradient(145deg, rgba(20,20,20,0.96), rgba(10,10,10,0.96))",
            border: "2px solid white",
            boxShadow: "0 22px 70px rgba(0,0,0,0.65)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            background:
              "linear-gradient(145deg, rgba(20,20,20,0.96), rgba(10,10,10,0.96))",
            color: "white",
          }}
        >
          {selectedRecipe && (
            <>
              {/* Image header */}
              <div style={{ position: "relative" }}>
                <img
                  src={selectedRecipe.image}
                  alt={selectedRecipe.label}
                  style={{
                    width: "100%",
                    height: "240px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                {/* Overlay gradient */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 100%)",
                  }}
                />

                {/* Title */}
                <div
                  style={{
                    position: "absolute",
                    left: 20,
                    right: 20,
                    bottom: 16,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: 0.3,
                      textShadow: "0 6px 18px rgba(0,0,0,0.6)",
                    }}
                  >
                    {selectedRecipe.label}
                  </Typography>
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: "20px" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 900,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Ingredients
                </Typography>

                {/* Ingredient chips */}
                <div
                  data-testid="ingredient-list"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "18px",
                  }}
                >
                  {selectedRecipe.ingredientLines.map((ingredient, index) => (
                    <span
                      key={index}
                      style={{
                        fontSize: "13px",
                        padding: "8px 12px",
                        borderRadius: "999px",
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        backdropFilter: "blur(8px)",
                        color: "rgba(255,255,255,0.95)",
                      }}
                    >
                      {ingredient}
                    </span>
                  ))}
                </div>

                {/* Source link */}
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.65)",
                    mb: 2,
                  }}
                >
                  Source:&nbsp;
                  <a
                    href={selectedRecipe.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#ffb74d",
                      fontWeight: 700,
                      textDecoration: "none",
                    }}
                  >
                    View original recipe →
                  </a>
                </Typography>

                {/* Close button */}
                <Button
                  onClick={handleClosePopup}
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 1,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 900,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: "black",
                    background: "linear-gradient(90deg, #ffb74d, #ffa726)",
                    boxShadow: "0 14px 30px rgba(0,0,0,0.4)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #ffc266, #ffb74d)",
                      boxShadow: "0 18px 40px rgba(0,0,0,0.55)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Recipes;
